import React, { Component, createContext, ReactNode } from 'react';
import parse from 'html-react-parser';
import { Brands } from '@core-ui/react-stitch';
import { Market } from '@core-ui/app-state-provider';
import { buildFacetQuery, buildUrlHash, getUpdatedListOfAppliedFacets } from '@seo/facet-adapter';
import { ProductGroup } from '@core-ui/product-grid/types';
import getPageData from '../../../state-builder/page-data';
import { ThematicResponse } from '../../../types/thematic-response';
import { AppliedFacets, NewFacetSelectionType, InlineFacetTag, FacetData } from '../../../types/facets';
import { BreadcrumbsData } from '../../../types/app';
import { MappedBloomreachResponse } from '../../../types/mapped-bloomreach-response';
import navLinks, { NavLinks } from './static-page-data/nav-links';
import bloomreachResponseMapper from './adapters/bloomreach-response-mapper';
import facetDataAdapter from './adapters/facet';
import productGridDataAdapter from './adapters/product-grid';
import inlineFacetTagDataAdapter from './adapters/inline-facet-tag';
import getAppliedFacetsCount from './adapters/applied-facets/get-applied-facets-count';
import removeAllFacetsFromAppliedArray from './adapters/applied-facets/clear-applied-facets';
import appendNavTrackingParams from './adapters/left-nav-analytics/append-tracking-params';


type PageDataProviderProps = {
  rawThematicApiData: Record<string, any>,
  brand: Brands,
  children: ReactNode,
  copyblock: string,
  enabledFeatures: Record<string, boolean>,
  market: Market,
  theme: string
} & Partial<BreadcrumbsData>;

type PageDataProviderState = {
  appliedFacets: AppliedFacets
};

export const PageDataContext = createContext<Record<string, any>>({});

export class PageDataProvider extends Component<PageDataProviderProps, PageDataProviderState> {
  constructor(props: PageDataProviderProps) {
    super(props);
    this.state = { appliedFacets: {} };
    this.computeComponentData(props.rawThematicApiData, props.brand);
    this.hashChangeHandler = this.hashChangeHandler.bind(this);
    this.clearAllFilters = this.clearAllFilters.bind(this);
    this.updateFacets = this.updateFacets.bind(this);
  }

  componentDidMount() {
    globalThis.window.addEventListener('hashchange', this.hashChangeHandler);
  }

  componentWillUnmount() {
    globalThis.window.removeEventListener('hashchange', this.hashChangeHandler);
  }

  private appliedFacetsCount: number;
  private copyblock: ReactNode;
  private facetData: FacetData;
  private facetTags: InlineFacetTag[];
  private h1: string;
  private mappedBloomreachResponse: MappedBloomreachResponse;
  private navData: NavLinks[Brands];
  private productGroups: ProductGroup[];

  computeComponentData(rawThematicApiData: ThematicResponse, brand: Brands): void {
    this.h1 = rawThematicApiData?.page_header?.h1 || '';
    this.copyblock = parse(this.props.copyblock);
    this.mappedBloomreachResponse = bloomreachResponseMapper(rawThematicApiData, this.props.enabledFeatures);
    this.productGroups = productGridDataAdapter(this.mappedBloomreachResponse);
    this.facetData = facetDataAdapter(this.mappedBloomreachResponse);
    this.facetTags = inlineFacetTagDataAdapter(this.facetData.appliedFacets);
    this.appliedFacetsCount = getAppliedFacetsCount(this.facetData.appliedFacets);
    this.navData = appendNavTrackingParams(navLinks?.[brand], this.props.theme) ?? [];
  }

  hashChangeHandler(): void {
    const { brand, market, theme, enabledFeatures } = this.props;
    const parameters = globalThis.window?.location?.hash?.substring(1)?.split('&') ?? [];
    const hashString = parameters.filter(Boolean).join('&');
    const isSkuSizesEnabled = enabledFeatures.enable_sku_size_bloomreach;
    const facetQueryArray = buildFacetQuery(hashString, { isSkuSizesEnabled });
    const efq = enabledFeatures['seo-filter-dropship-products'] ? ['seo-filter-dropship-products'] : [];

    getPageData({ brand, market, theme, facetQueryArray, efq })
      .then(({ rawThematicApiData }) => this.computeComponentData(rawThematicApiData, brand))
      .then(() => {
        const updatedFacets = { ...this.facetData.appliedFacets };

        this.setState(prevState => ({
          appliedFacets: {
            ...prevState.appliedFacets,
            ...updatedFacets
          }
        }));
      });
  }

  updateFacets(newFacetSelection: NewFacetSelectionType): void {
    const appliedFacets = { ...this.state.appliedFacets };
    const updatedFacets = getUpdatedListOfAppliedFacets(newFacetSelection, appliedFacets);
    const hashString = buildUrlHash({ ...updatedFacets });

    window.location.hash = hashString;
  }

  clearAllFilters(): void {
    const appliedFacets = { ...this.state.appliedFacets };
    const updatedFacets = removeAllFacetsFromAppliedArray(appliedFacets);
    const hashString = buildUrlHash({ ...updatedFacets });

    window.location.hash = hashString;
  }

  render() {
    const { rawThematicApiData, children, breadcrumbs } = this.props;

    return (
      <PageDataContext.Provider
        value={{
          appliedFacetsCount: this.appliedFacetsCount,
          clearAllFilters: this.clearAllFilters,
          copyblock: this.copyblock,
          headerText: this.h1,
          facetData: this.facetData,
          facetTags: this.facetTags,
          navData: this.navData,
          onFacetChange: this.updateFacets,
          productGroups: this.productGroups,
          breadcrumbs,
          ...(rawThematicApiData?.error && { error: rawThematicApiData?.error })
        }}
      >
        {children}
      </PageDataContext.Provider>
    );
  }
}
